import { Target, X } from 'lucide-react';
import React, { useState } from 'react';

/**
 * Professional Results Limit Modal
 * Based on conversation-mockup.html design
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Modal visibility
 * @param {Function} props.onClose - Close handler
 * @param {number} props.currentLimit - Current results limit
 * @param {Function} props.onUpdate - Limit update handler
 */
export function ResultsLimitModal({
  isOpen,
  onClose,
  currentLimit = 50,
  onUpdate
}) {
  const [selectedLimit, setSelectedLimit] = useState(currentLimit);
  const [customLimit, setCustomLimit] = useState('');

  const presetLimits = [10, 25, 50, 100, 200, 500];

  const handleSave = () => {
    const limit = customLimit ? parseInt(customLimit) : selectedLimit;
    if (limit > 0 && limit <= 1000) {
      onUpdate(limit);
      onClose();
    }
  };

  const handlePresetSelect = (limit) => {
    setSelectedLimit(limit);
    setCustomLimit('');
  };

  const handleCustomChange = (e) => {
    const value = e.target.value;
    if (value === '' || (/^\d+$/.test(value) && parseInt(value) <= 1000)) {
      setCustomLimit(value);
      if (value) {
        setSelectedLimit(parseInt(value));
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Target className="w-5 h-5 text-[#f7911d]" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Search Results Limit</h2>
              <p className="text-sm text-gray-500 mt-0.5">Set maximum candidates to search</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="px-6 py-6">
          {/* Current Limit Display */}
          <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Current Limit</span>
              <span className="text-2xl font-bold text-[#f7911d]">{currentLimit}</span>
            </div>
            <p className="text-xs text-gray-600 mt-1">
              ALICE will search up to {currentLimit} candidates
            </p>
          </div>

          {/* Preset Limits */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Quick Select
            </label>
            <div className="grid grid-cols-3 gap-2">
              {presetLimits.map((limit) => (
                <button
                  key={limit}
                  onClick={() => handlePresetSelect(limit)}
                  className={`px-4 py-3 rounded-lg border-2 transition font-medium text-sm ${
                    selectedLimit === limit && !customLimit
                      ? 'border-[#f7911d] bg-orange-50 text-[#f7911d]'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                >
                  {limit}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Limit */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Custom Limit
            </label>
            <input
              type="text"
              value={customLimit}
              onChange={handleCustomChange}
              placeholder="Enter custom limit (max 1000)"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#f7911d] focus:border-transparent text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              Maximum allowed: 1000 candidates
            </p>
          </div>

          {/* Info Box */}
          <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex gap-2">
              <div className="text-blue-600 mt-0.5">ℹ️</div>
              <div className="text-xs text-blue-900">
                <p className="font-medium mb-1">Performance Tip</p>
                <p>Higher limits may take longer to process. For best performance, start with 50-100 candidates.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition font-medium text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={selectedLimit === currentLimit && !customLimit}
            className="px-6 py-2 bg-[#f7911d] text-white rounded-lg hover:bg-[#e67e0a] transition font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Update Limit
          </button>
        </div>
      </div>
    </div>
  );
}

export default ResultsLimitModal;























